import { useState, useEffect } from 'react';
import { BarChart2, AlertCircle } from 'lucide-react';
import { Card, ProgressBar } from 'react-bootstrap';
import { apiService } from '../../../../services/apiService';
import { useAuth } from '../../../../contexts/AuthContext';
import { API_ENDPOINTS } from "../../../../config/api.config";
import ContentSkeleton from '../../../../components/loading/ContentSkeleton';

interface UsageData {
    usage: {
        storage_gb: number;
        kb_count: number;
        conversations_used: number;
        data_sources_count: number;
        indexed_pages_count: number;
    };
    limits: {
        storage_limit_gb: number;
        max_knowledge_bases: number;
        data_sources_limit: number;
        conversations_limit: number;
        indexed_pages_limit: number;
    };
    period: {
        start: string;
        end: string;
    };
    subscription: {
        id: string;
        status: string;
        plan: {
            id: string;
            name: string;
            billing_interval: string;
        };
    };
}

const formatDate = (dateStr: string) => {
    return new Date(dateStr).toLocaleDateString('en-US', {
        month: 'long',
        day: 'numeric',
        year: 'numeric'
    });
};

const UsagePage = () => {
    const auth = useAuth();
    const [usageData, setUsageData] = useState<UsageData | null>(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState<string | null>(null);

    useEffect(() => {
        fetchUsageData();
    }, []);

    const fetchUsageData = async () => {
        try {
            setLoading(true);
            const response = await apiService.get<{ data: UsageData }>(API_ENDPOINTS.BILLING.USAGE.CURRENT);
            setUsageData(response.data);
        } catch (err) {
            setError(err instanceof Error ? err.message : 'Failed to load usage data');
        } finally {
            setLoading(false);
        }
    };

    const calculatePercentage = (used: number, limit: number): number => {
        return Math.min(Math.round((used / limit) * 100), 100);
    };

    const getProgressVariant = (percentage: number): string => {
        if (percentage >= 90) return 'danger';
        if (percentage >= 75) return 'warning';
        return 'primary';
    };

    const formatNumber = (num: number | undefined): string => {
        if (num === undefined || num === null) return '0';
        if (num >= 1000000) {
            return (num / 1000000).toFixed(1) + 'M';
        } else if (num >= 1000) {
            return (num / 1000).toFixed(1) + 'K';
        }
        return num.toString();
    };

    const UsageProgressBar = ({
                                  used = 0,
                                  limit = 1,
                                  label
                              }: {
        used: number;
        limit: number;
        label: string
    }) => {
        const percentage = calculatePercentage(used || 0, limit || 1);
        const variant = getProgressVariant(percentage);

        return (
            <div className="mb-4">
                <div className="d-flex justify-content-between align-items-center mb-2">
                    <span>{label}</span>
                    <span className="text-muted">
                        {formatNumber(used)} / {formatNumber(limit)}
                    </span>
                </div>
                <ProgressBar
                    now={percentage}
                    variant={variant}
                    style={{ height: '10px' }}
                />
            </div>
        );
    };

    if (!auth.user) return null;

    return (
        <div className="page-container">
            <h5 className="mb-3">Resource Usage</h5>
            {loading ? (
                <ContentSkeleton />
            ) : error ? (
                <div className="alert alert-danger d-flex align-items-center" role="alert">
                    <AlertCircle size={20} className="me-2" />
                    {error}
                </div>
            ) : usageData ? (
                <Card className="border-0 shadow-sm">
                    <Card.Body className="p-4">
                        <div className="d-flex align-items-center mb-4">
                            <BarChart2 size={24} className="text-primary me-2" />
                            <div>
                                <h6 className="mb-0">Current Usage Period</h6>
                                <small className="text-muted">
                                    {formatDate(usageData.period.start)} - {formatDate(usageData.period.end)}
                                </small>
                            </div>
                        </div>

                        <div className="row">
                            <div className="col-lg-6">
                                <Card className="mb-4">
                                    <Card.Header className="bg-transparent">
                                        <h6 className="mb-0">Content Resources</h6>
                                    </Card.Header>
                                    <Card.Body>
                                        <UsageProgressBar
                                            used={usageData.usage.kb_count}
                                            limit={usageData.limits.max_knowledge_bases}
                                            label="Knowledge Bases"
                                        />
                                        <UsageProgressBar
                                            used={usageData.usage.data_sources_count}
                                            limit={usageData.limits.data_sources_limit}
                                            label="Data Sources"
                                        />
                                        <UsageProgressBar
                                            used={usageData.usage.indexed_pages_count}
                                            limit={usageData.limits.indexed_pages_limit}
                                            label="Indexed Pages"
                                        />
                                    </Card.Body>
                                </Card>
                            </div>

                            <div className="col-lg-6">
                                <Card className="mb-4">
                                    <Card.Header className="bg-transparent">
                                        <h6 className="mb-0">System Usage</h6>
                                    </Card.Header>
                                    <Card.Body>
                                        <UsageProgressBar
                                            used={usageData.usage.conversations_used}
                                            limit={usageData.limits.conversations_limit}
                                            label="Conversations"
                                        />
                                        <UsageProgressBar
                                            used={usageData.usage.storage_gb}
                                            limit={usageData.limits.storage_limit_gb}
                                            label="Storage (GB)"
                                        />
                                    </Card.Body>
                                </Card>
                            </div>
                        </div>

                        <div className="alert alert-info mt-3">
                            <small>
                                Note: Usage data is updated every hour.
                                {usageData.subscription ? (
                                    <>Your current plan is{' '}
                                        <strong>{usageData.subscription.plan.name}</strong>. To upgrade your
                                        limits, visit the subscription page.
                                    </>
                                ) : (
                                    <>You are currently on the free tier. To increase your limits,
                                        visit the subscription page to upgrade.
                                    </>
                                )}
                            </small>
                        </div>
                    </Card.Body>
                </Card>
            ) : (
                <div className="text-center p-4">
                    <p className="text-muted">No usage data available</p>
                </div>
            )}
        </div>
    );
};

export default UsagePage;